// src/features/apikey/PolicyForm.tsx
// Create/edit form for an API key policy — limits, TTL, rate limits, allowed permission
// sections, security requirements. Shared between create and edit in ApikeyPoliciesPage.
//
// FormDialog shell per docs/ux/14-forms.md. Policies have no dedicated detail surface to edit
// in place, so — like ApikeyListPage's edit dialog — this is the "heavy composer" exception:
// a label-left FormRow grid lives inside the dialog rather than a card of labeled inputs.
// ApikeyPoliciesPage still mounts this only while create/edit is toggled, so component state
// resets for free on each open (no `open` prop needed here).

import { useState } from 'react';
import { useI18n } from '@react/hooks';
import { Input } from '@react/components';
import { FormDialog, TitleInput, AutoGrowTextarea, FormRow } from '@/components/form';
import { ToggleSwitch } from '@/components/ToggleSwitch';
import {
  EMPTY_POLICY_FORM, validatePolicyForm,
} from './logic';
import type { PolicyFormState } from './logic';

const PERMISSION_SECTION_IDS = [
  'tasks', 'projects', 'documents', 'chat', 'forms', 'workflows',
  'billing', 'teams', 'calendar', 'notifications', 'users', 'apikeys',
];

export interface PolicyFormProps {
  initial?: PolicyFormState;
  title: string;
  submitLabel: string;
  isSaving: boolean;
  onSubmit: (data: PolicyFormState) => void;
  onCancel: () => void;
}

export function PolicyForm({ initial, title, submitLabel, isSaving, onSubmit, onCancel }: PolicyFormProps) {
  const { t } = useI18n();
  const [form, setForm] = useState<PolicyFormState>(initial ?? { ...EMPTY_POLICY_FORM });

  const set = <K extends keyof PolicyFormState>(field: K, value: PolicyFormState[K]) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleSection = (section: string) => {
    set(
      'allowedPermissionSections',
      form.allowedPermissionSections.includes(section)
        ? form.allowedPermissionSections.filter((s) => s !== section)
        : [...form.allowedPermissionSections, section],
    );
  };

  const canSubmit = Object.keys(validatePolicyForm(form)).length === 0;
  const dirty = JSON.stringify(form) !== JSON.stringify(initial ?? EMPTY_POLICY_FORM);

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(form);
  };

  return (
    <FormDialog
      open
      onClose={onCancel}
      onSubmit={submit}
      submitLabel={submitLabel}
      canSubmit={canSubmit}
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={title}
    >
      <div className="space-y-3 px-3.5 pb-4 pt-2">
        <TitleInput
          value={form.name}
          onChange={(v) => set('name', v)}
          placeholder={t('apikey.policy.field.namePlaceholder')}
          initialFocus
          onEnter={() => document.getElementById('apikey-policy-description')?.focus()}
          aria-label={t('apikey.policy.field.name')}
        />
        <AutoGrowTextarea
          id="apikey-policy-description"
          value={form.description}
          onChange={(v) => set('description', v)}
          placeholder={t('apikey.policy.field.descriptionPlaceholder')}
          minRows={1}
        />

        <FormRow
          label={t('apikey.policy.field.scopeEntityType')}
          hint={t('apikey.policy.field.scopeEntityTypeHelp')}
          labelWidth="sm"
        >
          <Input
            value={form.scopeEntityType}
            onChange={(e) => set('scopeEntityType', e.target.value)}
            placeholder={t('apikey.policy.field.scopeEntityTypePlaceholder')}
          />
        </FormRow>

        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <FormRow label={t('apikey.policy.field.maxKeys')} labelWidth="sm">
            <Input
              value={form.maxKeys}
              onChange={(e) => set('maxKeys', e.target.value)}
              placeholder={t('apikey.policy.field.unlimitedPlaceholder')}
              inputMode="numeric"
              aria-label={t('apikey.policy.field.maxKeys')}
            />
          </FormRow>
          <FormRow label={t('apikey.policy.field.maxKeysPerUser')} labelWidth="sm">
            <Input
              value={form.maxKeysPerUser}
              onChange={(e) => set('maxKeysPerUser', e.target.value)}
              placeholder={t('apikey.policy.field.unlimitedPlaceholder')}
              inputMode="numeric"
              aria-label={t('apikey.policy.field.maxKeysPerUser')}
            />
          </FormRow>
          <FormRow label={t('apikey.policy.field.defaultTtlDays')} labelWidth="sm">
            <Input
              value={form.defaultTtlDays}
              onChange={(e) => set('defaultTtlDays', e.target.value)}
              placeholder={t('apikey.policy.field.noDefaultPlaceholder')}
              inputMode="numeric"
              aria-label={t('apikey.policy.field.defaultTtlDays')}
            />
          </FormRow>
          <FormRow label={t('apikey.policy.field.maxTtlDays')} labelWidth="sm">
            <Input
              value={form.maxTtlDays}
              onChange={(e) => set('maxTtlDays', e.target.value)}
              placeholder={t('apikey.policy.field.unlimitedPlaceholder')}
              inputMode="numeric"
              aria-label={t('apikey.policy.field.maxTtlDays')}
            />
          </FormRow>
          <FormRow label={t('apikey.policy.field.maxRequestsPerMinute')} labelWidth="sm">
            <Input
              value={form.maxRequestsPerMinute}
              onChange={(e) => set('maxRequestsPerMinute', e.target.value)}
              placeholder={t('apikey.policy.field.unlimitedPlaceholder')}
              inputMode="numeric"
              aria-label={t('apikey.policy.field.maxRequestsPerMinute')}
            />
          </FormRow>
        </div>

        <div className="space-y-1 pt-1">
          <ToggleSwitch
            label={t('apikey.policy.field.allowNeverExpires')}
            checked={form.allowNeverExpires}
            onChange={(v) => set('allowNeverExpires', v)}
          />
          <ToggleSwitch
            label={t('apikey.policy.field.requireExpiration')}
            checked={form.requireExpiration}
            onChange={(v) => set('requireExpiration', v)}
          />
          <ToggleSwitch
            label={t('apikey.policy.field.requireIpRestrictions')}
            checked={form.requireIpRestrictions}
            onChange={(v) => set('requireIpRestrictions', v)}
          />
        </div>

        <div className="space-y-2 pt-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t('apikey.policy.field.allowedPermissionSections')}
          </p>
          <p className="text-xs text-muted-foreground">{t('apikey.policy.field.allowedPermissionSectionsHelp')}</p>
          <div className="flex flex-wrap gap-2">
            {PERMISSION_SECTION_IDS.map((section) => (
              <button
                key={section}
                type="button"
                onClick={() => toggleSection(section)}
                className={`inline-flex items-center px-3 py-1.5 rounded-md text-xs font-medium border transition-colors ${
                  form.allowedPermissionSections.includes(section)
                    ? 'bg-primary text-primary-foreground border-primary'
                    : 'bg-background text-muted-foreground border-border hover:border-foreground/20'
                }`}
              >
                {t(`permissions.sections.${section}`)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </FormDialog>
  );
}
